import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Palmtree, Loader2 } from "lucide-react";
import { toast } from "sonner";

const routeFor = (role) => (role === "admin" ? "/admin" : role === "pm" ? "/pm" : "/vendor");

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(routeFor(u.role));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B192C]">
      <div className="hidden lg:flex flex-col justify-between p-12 grain relative">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <div className="w-9 h-9 rounded-lg bg-sunburst flex items-center justify-center">
            <Palmtree className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-white">Florida Property Assistants</span>
        </Link>
        <div className="relative z-10">
          <h2 className="text-3xl font-display font-black text-white leading-tight">One dashboard.<br /><span className="text-sunburst">Every property job.</span></h2>
          <p className="mt-4 text-white/60 max-w-sm">Dispatch, price, and track property services across Florida — with transparent role-based access.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-display font-bold text-slate-900">Sign in</h1>
          <p className="text-slate-500 mt-1 text-sm">Access your Florida Property Assistants portal.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" placeholder="you@company.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-[#0B192C] hover:bg-[#1E3E62] text-white font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            No account? <Link to="/register" className="text-ocean font-semibold">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
